import { atom, onMount } from "nanostores";
import z from "zod";
import type { definition } from "@/server/api/definition";
import { lifeError } from "@/shared/error";
import { defineAgentAtom } from "./define";

type AgentInfoResponse = z.infer<(typeof definition)["agent.info"]["outputDataSchema"]>;

const atomConfigSchema = z.object({
  pollingMs: z.number().min(1000).max(30_000).prefault(5000),
});

export const agentInfoAtomDef = defineAgentAtom(({ agent }) => ({
  name: "info",
  create: (config: z.input<typeof atomConfigSchema>) => {
    // Validate config
    const { error: errConfig, data: parsedConfig } = atomConfigSchema.safeParse(config ?? {});
    if (errConfig)
      throw lifeError({ code: "Validation", message: "Invalid config provided to atom." });
    // Create the store and the refresh function
    const store = atom<AgentInfoResponse | null>(null);
    const refresh = async () => {
      try {
        const [error, data] = await agent.info();
        if (error) throw error;
        store.set(data);
      } catch (error) {
        throw lifeError({ code: "Unknown", cause: error });
      }
    };
    onMount(store, () => {
      // Fetch immediately on mount
      refresh();
      // Set up polling interval
      const intervalId = setInterval(() => refresh(), parsedConfig.pollingMs);
      // Cleanup on unmount
      return () => clearInterval(intervalId);
    });
    return { store, refresh };
  },
}));
